import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../context/auth/AuthContext";
import { auth } from "../firebase";
import { useAppDispatch } from "./useRedux";
import { handleClearPractice } from "../redux/features/practice/practiceSlice";
import { setUnauthorizedLogoutHandler } from "../utils/axios";
import { clearClientSession } from "../utils/clientSessionStore";
import { clearOtpSession } from "../utils/otpSessionStore";
import { USER_DETAILS_QUERY_KEY } from "../modules/auth/constants/auth.constants";

export default function useAuthContext() {
    const user = React.useContext(AuthContext);

    const dispatch = useAppDispatch();

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const isLoggingOutRef = React.useRef(false);

    const logOutUser = React.useCallback(async () => {
        if (isLoggingOutRef.current) {
            return;
        }

        isLoggingOutRef.current = true;

        await queryClient.cancelQueries({ queryKey: USER_DETAILS_QUERY_KEY });
        if (user?.uid) {
            clearOtpSession(user.uid);
        }
        clearClientSession();
        dispatch(handleClearPractice());
        localStorage.removeItem("from");
        try {
            await auth.signOut();
        } finally {
            queryClient.clear();
            navigate("/login", { replace: true });
            isLoggingOutRef.current = false;
        }
    }, [dispatch, navigate, queryClient, user?.uid]);

    React.useEffect(() => {
        setUnauthorizedLogoutHandler(logOutUser);

        return () => {
            setUnauthorizedLogoutHandler(null);
        };
    }, [logOutUser]);

    return { user, logOutUser };
}
